/* eslint-disable react-hooks/rules-of-hooks */
import React, { Fragment, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSDMCustomerContext, useSDMViewportContext } from '@ldp/sdm-contexts';
import { WithRendererCallbacks, useViewResolverContext } from '@ldp/view-resolver-lite';
import {
  LOBs,
  createMediaRequestBody,
  generateMediaDisplayInfo,
  generateMediaMeta,
  getAbsoluteLink,
  getAdSizesByViewport,
  getCiamAccessToken,
  getIsSourceFiltered,
  getSDMUrlParams,
  useLazyLoad,
} from '@ldp/media-utils';
import { useFetchAds } from '../../hooks/useFetchAds';

import { ErrorBoundary } from '@ldp/error-boundary';
import { WrapperSection } from '@ldp-web/component-library/ui/atoms/section';
import { IMediaWrapper, TViewport } from './media-wrapper.interface';
import uatConfig from '../../configs/config.uat.json';
import qaConfig from '../../configs/config.qa.json';
import devConfig from '../../configs/config.development.json';
import useSkeletonMap from '../../hooks/use-skeleton-map';

export interface IMediaWrapperComponentProps extends IMediaWrapper {
  componentId: string;
  contentId: string;
  cacheBuster?: string | number;
}

/** With Media HOC Component
 * its responsible for next gen sponsored display data fetching,
 * and fires analytics calls when the data arrives
 */
export const MediaWrapperComponent = <P extends object>(WrappedComponent: React.ComponentType<any>) =>
  function MediaWrapper(props: P & IMediaWrapperComponentProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { fetchOptions, viewDefinition } = useViewResolverContext();
    const { pcOptimumAccount, customerId } = useSDMCustomerContext();
    const walletId = pcOptimumAccount?.memberId;
    const { viewport } = useSDMViewportContext();
    const skeletonMap = useSkeletonMap();

    const { componentId, mediaConfiguration, contentId, cacheBuster = null } = props;
    const componentSkeleton = skeletonMap[componentId]?.() ?? null;
    const traceIdRef = useRef<string>(uuidv4());

    const { originalTargetParam, mediaUATParam, sourceParam } = getSDMUrlParams();

    const {
      adUnitType,
      adSizesMobile,
      adSizesTablet,
      adSizesDesktop,
      lazyLoad,
      lazyLoadingTriggerThreshold,
      position,
      searchTerm,
      customData,
      pageType,
    } = mediaConfiguration;

    const term = window.location.pathname === '/search' && searchTerm ? searchTerm : originalTargetParam;

    const { inViewport } = useLazyLoad(lazyLoad, containerRef, lazyLoadingTriggerThreshold);

    const adSizes = getAdSizesByViewport(viewport as TViewport, adSizesMobile, adSizesTablet, adSizesDesktop);
    const mediaDisplayInfo = generateMediaDisplayInfo({
      adUnitType,
      adSizesByViewport: adSizes,
      searchTerm: term,
    });

    const adPlacementId = `${adUnitType}_${position}`;
    const targetPageType = pageType || viewDefinition?.viewAnalytics?.mediaAnalytics?.target_page_type;

    const meta = generateMediaMeta({
      targetPageType: targetPageType as string,
      adPlacementId,
      customData,
      Lob: LOBs.SDM,
    });
    const ciamAccessToken = getCiamAccessToken();
    const isLoggedIn = Boolean(ciamAccessToken && customerId);
    const banner = window.location?.hostname.includes('pharmaprix') ? 'pharmaprix' : 'shoppersdrugmart';

    const isSourceFiltered = getIsSourceFiltered(sourceParam, props as Record<string, unknown>);

    const { reservedId, ...requestBody } = createMediaRequestBody({
      fetchOptions,
      searchTerm: term,
      meta,
      mediaDisplayInfo,
      customerId,
      walletId,
      targetPageType,
      adUnitType,
      position,
      componentId,
      Lob: LOBs.SDM,
      customData,
    });

    const { data, isFetching, error } = useFetchAds({
      fetchOptions: {
        ...fetchOptions,
        body: JSON.stringify(requestBody),
      },
      endpoint: `${banner}/media/ads`,
      ...(isLoggedIn && { ciamAccessToken }),
      ...(mediaUATParam == 'dev' && { envConfig: devConfig }),
      ...(mediaUATParam == 'qa' && { envConfig: qaConfig }),
      ...((mediaUATParam == 'uat' || mediaUATParam == 'true') && {
        envConfig: uatConfig,
      }),
      cacheBuster,
      enabled: !isSourceFiltered, // Only enable when client is ready and QueryClient is available
      reservedId,
      // @ts-ignore
      traceId: traceIdRef.current,
    });

    if (isFetching) {
      return componentSkeleton;
    } else if (error || !data) {
      return null;
    }

    const { components } = data || {};

    const LayoutWrapper = targetPageType === 'homepage' ? WrapperSection : Fragment;

    return (
      <ErrorBoundary FallbackComponent={() => null}>
        {components?.map((component: any) => {
          const { data } = component;

          // RMP-10050: fix issue with SDM react router caching data on back button impacting events
          if (data?.callToAction?.url) {
            data.callToAction.url = getAbsoluteLink(data.callToAction.url);
          }

          return (
            <div
              data-testid={`with-media-component-${position}`}
              key={`${data.componentId}-${position}`}
              ref={containerRef}
            >
              <LayoutWrapper marginBottom={0}>
                <WithRendererCallbacks
                  {...data}
                  componentId={componentId}
                  id={position}
                  checkViewed={inViewport}
                  contentId={contentId}
                />
                <WrappedComponent {...data} componentId={componentId} contentId={contentId} />
              </LayoutWrapper>
            </div>
          );
        })}
      </ErrorBoundary>
    );
  };

export default MediaWrapperComponent;
